import { useState } from 'react';
import {
  amountPayload,
  buildAmountRows,
  fillPreviousFromPrior,
  priorByName,
  type EditableAmountRow,
  type NoteDialogData,
  type NoteRow,
  type NoteType,
} from '@/core/models/notes.models';
import type { DialogComponentProps } from '@/kit/dialog';
import { InputField, TextareaField } from '@/kit/FormField';
import { RequiredMark } from '@/kit/RequiredMark';
import { AmountRowsTable } from './AmountRowsTable';
import { FieldMessage, NoteDialogShell, useNoteSave } from './NoteDialogParts';

/** Blank previous-year cells default from the prior period's entry with the same name. */
export function withPrior(rows: EditableAmountRow[], data: NoteDialogData | null, name: string): EditableAmountRow[] {
  const copy = rows.map((row) => ({ ...row }));
  if (data) {
    fillPreviousFromPrior(copy, priorByName(data, name));
  }
  return copy;
}

interface NamedAmountDialogProps extends DialogComponentProps<NoteDialogData, NoteRow> {
  noteType: NoteType;
  title: string;
  /** advance-credit wraps the body in `pl-5 pr-5` and has a shorter grid. */
  bodyClassName?: string;
  rootClassName?: string;
}

/** Name + description + amount grid dialog (guarantees, advances and credits). */
function NamedAmountDialog({ data, dialogRef, noteType, title, bodyClassName, rootClassName }: NamedAmountDialogProps) {
  const content = data?.note?.content ?? {};
  const [model, setModel] = useState({ name: content.name ?? '', description: content.description ?? '' });
  const [rows, setRows] = useState<EditableAmountRow[]>(() =>
    withPrior(buildAmountRows(noteType, content.rows), data, content.name ?? ''),
  );
  const { isSaving, save, submitted } = useNoteSave(data, dialogRef);
  const nameMissing = String(model.name).trim().length === 0;

  return (
    <NoteDialogShell
      title={title}
      rootClassName={rootClassName}
      isSaving={isSaving}
      onSave={() => save(() => ({ ...model, rows: amountPayload(rows) }), !nameMissing)}
    >
      <div className={bodyClassName}>
        <div className="form-field">
          <p className="fw-bold">
            Name <RequiredMark />
          </p>
          <InputField
            fieldClassName="wp-100"
            autoComplete="off"
            charMode="printable"
            maxLength={255}
            invalid={submitted && nameMissing}
            value={model.name}
            onChange={(event) => setModel((current) => ({ ...current, name: event.target.value }))}
            onBlur={() => setRows((current) => withPrior(current, data, model.name))}
          />
          <FieldMessage show={submitted && nameMissing}>Enter the name.</FieldMessage>
        </div>
        <div className="form-field textarea">
          <p className="fw-bold">Description of the transaction and general amounts involved</p>
          <TextareaField
            fieldClassName="wp-100"
            autoComplete="off"
            maxLength={4000}
            value={model.description}
            onChange={(event) => setModel((current) => ({ ...current, description: event.target.value }))}
          />
        </div>

        <AmountRowsTable
          rows={rows}
          onCellChange={(index, column, value) =>
            setRows((current) => current.map((row, i) => (i === index ? { ...row, [column]: value } : row)))
          }
        />
      </div>
    </NoteDialogShell>
  );
}

export function GuaranteesDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <NamedAmountDialog
      {...props}
      noteType="guarantee"
      title={props.data?.note ? 'Edit Guarantee' : 'Add New Guarantee'}
    />
  );
}

export function AdvanceCreditDialog(props: DialogComponentProps<NoteDialogData, NoteRow>) {
  return (
    <NamedAmountDialog
      {...props}
      noteType="advanceCredit"
      title={`${props.data?.note ? 'Edit' : 'Add'} Advance & Credit`}
      bodyClassName="pl-5 pr-5"
      rootClassName="notes-dialog-advance"
    />
  );
}
